import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import CodeMirror from '@uiw/react-codemirror';

import { EditorState } from '@codemirror/state';
import { StreamLanguage } from '@codemirror/language';
import { autocompletion } from '@codemirror/autocomplete';

import { filterBarTheme as defaultTheme } from './themes/filterBarTheme';

const { isArray } = Array;
const anyWordCharacterGreedyRE = /\w*/;

const defaultBasicSetup = {
    lineNumbers: true,
    highlightActiveLineGutter: true,
    highlightSpecialChars: true,
    history: false,
    foldGutter: false,
    drawSelection: true,
    dropCursor: false,
    allowMultipleSelections: false,
    indentOnInput: false,
    syntaxHighlighting: true,
    bracketMatching: true,
    closeBrackets: true,
    rectangularSelection: true,
    crosshairCursor: false,
    highlightActiveLine: false,
    highlightSelectionMatches: true,
    closeBracketsKeymap: false,
    defaultKeymap: false,
    searchKeymap: false,
    historyKeymap: false,
    foldKeymap: false,
    lintKeymap: false,
    completionKeymap: false,
    autocompletion: false
};

// todo, check: https://github.com/codesandbox/sandpack/issues/505 for 'RangeError Selection points outside of document'

const CodeEditor = ({ mode, setup, extensions, completions, ...rest }) => {
    const extensionsArray = isArray(extensions) ? [...extensions] : [];
    const themes = new Map();
    const params = {
        ...rest,
        theme: defaultTheme,
        extensions: extensionsArray,
        basicSetup: { ...defaultBasicSetup, ...setup }
    };
    if (mode) {
        const [extensionsRequired, theme] = useMemo(() => {
            const extensionsRequired = [StreamLanguage.define(mode)];
            const modeName = mode.name;
            switch (modeName) {
                case 'filterbar':
                    //RN: prevent new lines if mode is filterbar
                    extensionsRequired.push(
                        EditorState.transactionFilter.of(tr => {
                            return tr.newDoc.lines > 1 ? [] : [tr];
                        })
                    );
                    break;
            }
            return [extensionsRequired, themes.get(modeName)];
        }, [mode]);
        extensionsArray.push(...extensionsRequired);
        if (theme) {
            params.theme = theme;
        }
    }
    if (completions?.length) {
        extensionsArray.push(
            autocompletion({
                selectOnOpen: false,
                icons: false,
                override: [
                    ctx => {
                        // prettier-ignore
                        const {from, to} = ctx.matchBefore(anyWordCharacterGreedyRE);
                        return from === to && !ctx.explicit
                            ? null
                            : {
                                  from: from,
                                  options: completions,
                                  filter: true // 𝖿𝖺𝗅𝗌𝖾 𝘸𝘪𝘭𝘭 𝘴𝘩𝘰𝘸 𝘢𝘭𝘭 𝘤𝘰𝘮𝘱𝘭𝘦𝘵𝘪𝘰𝘯𝘴
                              };
                    }
                ]
            })
        );
    }
    return <CodeMirror {...params} />;
};

CodeEditor.propTypes = {
    mode: PropTypes.shape({
        name: PropTypes.string,
        startState: PropTypes.func,
        token: PropTypes.func
    }),
    setup: PropTypes.shape({
        lineNumbers: PropTypes.bool,
        highlightActiveLineGutter: PropTypes.bool,
        highlightSpecialChars: PropTypes.bool,
        history: PropTypes.bool,
        foldGutter: PropTypes.bool,
        drawSelection: PropTypes.bool,
        dropCursor: PropTypes.bool,
        allowMultipleSelections: PropTypes.bool,
        indentOnInput: PropTypes.bool,
        syntaxHighlighting: PropTypes.bool,
        bracketMatching: PropTypes.bool,
        closeBrackets: PropTypes.bool,
        rectangularSelection: PropTypes.bool,
        crosshairCursor: PropTypes.bool,
        highlightActiveLine: PropTypes.bool,
        highlightSelectionMatches: PropTypes.bool,
        closeBracketsKeymap: PropTypes.bool,
        defaultKeymap: PropTypes.bool,
        searchKeymap: PropTypes.bool,
        historyKeymap: PropTypes.bool,
        foldKeymap: PropTypes.bool,
        lintKeymap: PropTypes.bool,
        completionKeymap: PropTypes.bool,
        autocompletion: PropTypes.bool
    }),
    /**
     * Extension values can be [provided](https://codemirror.net/6/docs/ref/#state.EditorStateConfig.extensions) when creating a state to attach various kinds of configuration and behavior information.
     * They can either be built-in extension-providing objects,
     * such as [state fields](https://codemirror.net/6/docs/ref/#state.StateField) or [facet providers](https://codemirror.net/6/docs/ref/#state.Facet.of),
     * or objects with an extension in its `extension` property. Extensions can be nested in arrays arbitrarily deep—they will be flattened when processed.
     */
    extensions: PropTypes.array,
    value: PropTypes.string,
    autoFocus: PropTypes.bool,
    editable: PropTypes.bool,
    readOnly: PropTypes.bool,
    placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    /** default to 'auto' */
    height: PropTypes.string,
    minHeight: PropTypes.string,
    maxHeight: PropTypes.string,
    width: PropTypes.string,
    minWidth: PropTypes.string,
    maxWidth: PropTypes.string,
    /**
     * `light` or `dark` version of extension theme Defaults to `light`.
     * @default light
     */
    theme: PropTypes.oneOfType([
        PropTypes.oneOf(['light', 'dark']),
        PropTypes.object
    ]),

    /**
     * Whether to optional basicSetup by default
     * @default true
     */
    indentWithTab: PropTypes.bool,
    /** Fired whenever a change occurs to the document.
     *  function @params (value: string, viewUpdate: ViewUpdate)
     */
    onChange: PropTypes.func,
    /** Some data on the statistics editor.
     *  function @params (data: Statistics)
     * */
    onStatistics: PropTypes.func,
    /** The first time the editor executes the event.
     *  function @params (value: string, viewUpdate: ViewUpdate)
     * */
    onCreateEditor: PropTypes.func,
    /** Fired whenever any state change occurs within the editor, including non-document changes like lint results.
     *  function @params (value: string, viewUpdate: ViewUpdate)
     */
    onUpdate: PropTypes.func,
    /**
     * If the view is going to be mounted in a shadow root or document other than the one held by the global variable document (the default), you should pass it here.
     * Originally from the [config of EditorView](https://codemirror.net/6/docs/ref/#view.EditorView.constructor%5Econfig.root)
     */
    root: PropTypes.element,
    /**
     * Create a state from its JSON representation serialized with [toJSON](https://codemirror.net/docs/ref/#state.EditorState.toJSON) function
     */
    initialState: PropTypes.shape({
        json: PropTypes.object.isRequired,
        fields: PropTypes.object
    })
    /*
    onClick: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func
    */
};

export default CodeEditor;
